import React, { FC, useMemo, useState } from 'react';
import cx from 'classnames';
import { Button } from 'antd';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Document, Page } from 'appJS/utils/pdfjs_worker';
import styles from './styles.module.css';
import {
  displayedAssetVersion,
  getAssetTitle,
  sortDesignGroupAssets,
  versionsForAsset
} from './utils';
import { AssetVersion } from '../../../../clients/design_assets/types';
import VersionSelector from '../../../../clients/design_assets/VersionSelector';

export type DesignGroupNavigationAsset = {
  id: number;
  name?: string;
  fileName?: string;
  fileType?: string;
  groupName?: string;
  status?: string;
  shareToken?: string;
  url: string;
  versions?: AssetVersion[];
};

type Props = {
  assets: DesignGroupNavigationAsset[];
  collapsed: boolean;
  selectedAssetId?: number;
  onSelect: (
    asset: DesignGroupNavigationAsset,
    preferredVersion?: number
  ) => void;
  onToggle: () => void;
  versioningEnabled?: boolean;
  container?: HTMLElement;
  onGroupVersionChange?: (version: AssetVersion | 'all') => void;
};

const DesignGroupNavigation: FC<Props> = ({
  assets,
  collapsed,
  selectedAssetId,
  onSelect,
  onToggle,
  versioningEnabled,
  container,
  onGroupVersionChange
}) => {
  const sortedAssets = sortDesignGroupAssets(assets);
  const groupName = sortedAssets[0]?.groupName || 'Design group';
  const groupVersions = useMemo(() => {
    if (!versioningEnabled) return [];
    const unique = new Map<number, AssetVersion>();
    sortedAssets.forEach(asset => {
      versionsForAsset(asset).forEach(version => {
        if (!unique.has(version.version)) unique.set(version.version, version);
      });
    });
    return Array.from(unique.values()).sort((a, b) => a.version - b.version);
  }, [sortedAssets, versioningEnabled]);
  const [selectedGroupVersion, setSelectedGroupVersion] = useState<
    AssetVersion | 'all'
  >('all');

  const selectGroupVersion = (version: AssetVersion | 'all') => {
    setSelectedGroupVersion(version);
    onGroupVersionChange?.(version);
  };

  return (
    <aside
      className={cx(styles.navigation, { [styles.collapsed]: collapsed })}
      aria-label="Designs in this group"
    >
      <div className={styles.header}>
        {!collapsed && (
          <div className={styles.headerText}>
            <h2 title={groupName}>{groupName}</h2>
            <span>{sortedAssets.length} designs</span>
            {versioningEnabled && groupVersions.length > 0 && (
              <div className={styles.groupVersion}>
                <VersionSelector
                  versions={groupVersions}
                  selected={selectedGroupVersion}
                  onSelect={selectGroupVersion}
                  onSelectAll={() => selectGroupVersion('all')}
                  showAllOption
                  container={container}
                />
              </div>
            )}
          </div>
        )}
        <Button
          type="default"
          shape="circle"
          aria-label={
            collapsed ? 'Expand design navigation' : 'Collapse design navigation'
          }
          icon={
            collapsed ? (
              <ChevronRight className={styles.toggleIcon} />
            ) : (
              <ChevronLeft className={styles.toggleIcon} />
            )
          }
          onClick={onToggle}
          className={styles.toggleButton}
        />
      </div>

      <nav className={styles.assetList}>
        {sortedAssets.map(asset => {
          const selected = asset.id === selectedAssetId;
          const assetTitle = getAssetTitle(asset);
          const preview = displayedAssetVersion(asset, selectedGroupVersion);
          const isPdf = (preview?.fileType || asset.fileType) === 'pdf';
          const previewUrl = preview?.url || asset.url;
          const preferredVersion =
            selectedGroupVersion === 'all' ? undefined : preview?.version;

          return (
            <button
              key={asset.id}
              type="button"
              className={cx(styles.assetButton, {
                [styles.selected]: selected
              })}
              onClick={() => onSelect(asset, preferredVersion)}
              title={assetTitle}
              aria-current={selected ? 'page' : undefined}
            >
              <span className={styles.thumbnail}>
                {isPdf ? (
                  <Document file={previewUrl} loading={null}>
                    <Page
                      pageNumber={1}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      loading={null}
                    />
                  </Document>
                ) : (
                  <img src={previewUrl} alt="" crossOrigin="anonymous" />
                )}
              </span>
              {!collapsed && (
                <span className={styles.assetText}>
                  <strong>{assetTitle}</strong>
                  {asset.fileName && asset.fileName !== assetTitle && (
                    <span>{asset.fileName}</span>
                  )}
                </span>
              )}
            </button>
          );
        })}
      </nav>
    </aside>
  );
};

export default DesignGroupNavigation;
